import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Breadcrumbs, IconButton, Tooltip, Typography, makeStyles,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Box,
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import PageLayout from '../../PageLayout';
import Button from '../../controls/Button';
import DataTable from '../../DataTable/DataTable';
import { history, workFieldHelper, userHelper, dataTableHelper } from '../../../helpers';
import { providerActions } from '../../../redux/actions';
import { providersTexts } from '../../../constants';
import colors from '../../../assets/colors';
import { tableStyles } from '../../../common/constants';
import CircleButton from '../../controls/CircleButton';
import PopoverWarningMessage from '../../controls/PopoverWarningMessage';
import CustomDialogAlert from '../../controls/CustomDialogAlert';

const useStyles = makeStyles(() => ({
  tableBody: {
    color: 'black',
    textDecoration: 'none',
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
}));

const ProviderPage = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const userAuth = useSelector((state) => state.authentication.user);
  const deletingProvider = useSelector((state) => state.provider.deleting);
  const deletedProvider = useSelector((state) => state.provider.deleted);
  const noLinkedWorkField = useSelector((state) => state.provider.noLinkedWorkField);
  const hasAdminRoles = userHelper.isAdmin(userAuth.roles);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [providerID, setProviderID] = useState(null);

  const [openNoLinkedWorkFieldDialog, setOpenNoLinkedWorkFieldDialog] = useState(false);
  const noLinkedWorkFieldMessage = {
    title: providersTexts.noLinkedWorkFieldTitle,
    body: providersTexts.noLinkedWorkFieldBody,
  };

  useEffect(() => {
    if (noLinkedWorkField) {
      setOpenNoLinkedWorkFieldDialog(true);
    }
  }, [noLinkedWorkField]);

  useEffect(() => {
    if (deletedProvider) {
      setOpenDeleteDialog(false);
    }
  }, [deletedProvider]);

  function toggleDeleteDialog() {
    setOpenDeleteDialog(!openDeleteDialog);
  }

  function handleDeleteProviders(id) {
    dispatch(providerActions.deleteProvider(id, providersTexts.successfulDelete));
  }

  function toggleNoLinkedWorkFieldDialog() {
    setOpenNoLinkedWorkFieldDialog(!openNoLinkedWorkFieldDialog);
  }

  function ProviderPageBreadcrumbs() {
    return (
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Typography component={Link} to="/providers" variant="caption" color="textPrimary" style={{ fontWeight: 700, fontSize: 11, textDecoration: 'none' }}>
          {providersTexts.providers}
        </Typography>
      </Breadcrumbs>
    );
  }

  function ButtonGroup() {
    if (!hasAdminRoles) return null;

    return (
      <>
        <Button
          text={providersTexts.addButton}
          component={Link}
          to="providers/new"
          style={{ fontSize: '11px', fontWeight: 600 }}
        />
        <CircleButton
          text={(
            <Tooltip title={providersTexts.bulkCreationTitle}>
              <GroupAddIcon color="primary" fontSize="small" />
            </Tooltip>
          )}
          component={Link}
          to="providers/upload"
          variant="outlined"
        />
      </>
    );
  }

  function actionsButtons(link, id, isDeleted = false) {
    if (isDeleted) return null;

    return (
      <Box display="flex">
        <IconButton onClick={() => history.push(link)} style={{ textDecoration: 'none', padding: '0px', marginRight: '16px' }}>
          <Tooltip title={providersTexts.updateProvider}>
            <CreateIcon style={{ width: 16, height: 16, color: colors.orangeLight }} />
          </Tooltip>
        </IconButton>
        <IconButton onClick={() => { setProviderID(id); toggleDeleteDialog(); }} style={{ textDecoration: 'none', padding: '0px', marginRight: '16px' }}>
          <Tooltip title={providersTexts.deleteTitleDialog}>
            <DeleteRoundedIcon style={{ width: 16, height: 16, color: colors.greyLight }} />
          </Tooltip>
        </IconButton>
      </Box>
    );
  }

  function getWorkFieldsName(providerWorkField) {
    const entityLink = `/workFields/${providerWorkField.id}`;
    return (
      <Typography className={classes.tableBody} style={{ display: 'inline' }} component={Link} to={entityLink}>
        {providerWorkField.name}
      </Typography>
    );
  }

  const keys = [
    'id',
    'name',
    'workField',
    'cuit',
    'businessName',
    'lastWorkFieldEvaluationStatus',
    'lastWorkFieldEvaluationDate',
    'lastModificationDate',
    'lastModificationUser',
    ...(hasAdminRoles ? ['deleteDate', 'deleteUser'] : [])
  ];

  const columns = [
    {
      name: 'id',
      options: {
        display: 'excluded',
        filter: false,
      },
    },
    {
      name: 'name',
      label: intl.formatMessage({ id: 'app.name', defaultMessage: 'Nombre' }),
      options: {
        customBodyRender: (value, meta) => {
          const providerId = meta.rowData[0];
          const textTitle = (
            <Typography className={classes.tableBody} component={Link} to={`/providers/${providerId}`}>
              {value}
            </Typography>
          );
          return textTitle;
        },
      },
    },
    {
      name: 'workField',
      label: intl.formatMessage({ id: 'providers.workFields', defaultMessage: 'Rubros' }),
      options: {
        customBodyRender: (value, meta) => {
          const id = meta.rowData[0];
          if (value === '-') {
            return (
              <PopoverWarningMessage
                id={id}
                message={providersTexts.noLinkedWorkFielPopoverMessage}
                customStyle={{ maxWidth: '350px' }}
              />
            );
          }
          return getWorkFieldsName(value);
        },
        filter: false,
        customDownloadBodyRender: (value) => value.map((workField) => workField.workField.name).join(', '),
        sortCompare: (order) => workFieldHelper.sortCompare(order),
      },
    },
    {
      name: 'cuit',
      label: intl.formatMessage({ id: 'providers.cuit', defaultMessage: 'CUIT' }),
    },
    {
      name: 'businessName',
      label: intl.formatMessage({ id: 'providers.businessName', defaultMessage: 'Razón Social' }),
    },
    {
      name: 'lastWorkFieldEvaluationStatus',
      label: intl.formatMessage({ id: 'providers.lastWorkFieldEvaluations', defaultMessage: 'Resultado última evaluación' }),
    },
    {
      name: 'lastWorkFieldEvaluationDate',
      label: intl.formatMessage({ id: 'workFieldEvaluation.date', defaultMessage: 'Fecha de evaluación' }),
      options: {
        customBodyRender: (value) => {
          if (value && value > '01/01/1900') {
            return moment(value).format('DD/MM/YYYY');
          }
          return '-';
        },
      },
    },
    {
      name: 'lastModificationDate',
      label: intl.formatMessage({ id: 'entity.lastModificationDate', defaultMessage: 'Fecha de última modificación' }),
      options: {
        filter: false,
        customBodyRender: (date) => {
          if (date === '-') return date;
          return moment(date).format('DD/MM/YYYY HH:mm [hs.]');
        },
      },
    },
    {
      name: 'lastModificationUser',
      label: intl.formatMessage({ id: 'entity.lastModificationUser', defaultMessage: 'Usuario de última modificación' }),
      options: {
        filter: false,
      },
    },
    ...(hasAdminRoles ? [
      {
        name: 'deleteDate',
        label: intl.formatMessage({ id: 'entity.deleteDate', defaultMessage: 'Fecha de eliminación' }),
        options: {
          filter: false,
          customBodyRender: (date) => {
            if (date === '-') return date;
            return moment(date).format('DD/MM/YYYY HH:mm [hs.]');
          },
        },
      },
      {
        name: 'deleteUser',
        label: intl.formatMessage({ id: 'entity.deleteUser', defaultMessage: 'Usuario que eliminó' }),
        options: {
          filter: false,
        },
      },
      {
        name: 'actions',
        options: {
          filter: false,
          sort: false,
          download: false,
          customBodyRender: (_, meta) => {
            const providerId = meta.rowData[0];
            const link = `/providers/${providerId}/edit`;
            const isDeleted = dataTableHelper.isRowDeleted(keys, meta.rowData);
            return actionsButtons(link, providerId, isDeleted);
          },
          setCellHeaderProps: () => ({
            style: { display: 'none' },
          }),
          setCellProps: () => ({
            style: tableStyles.cellBody,
          }),
        },
      }
    ] : []),
  ];

  const fetchUrl = `companies/${userAuth.company.id}/providers/byWorkFields`;
  const itemUrl = 'providers/';

  return (
    <PageLayout
      title={providersTexts.providers}
      breadCrumbs={<ProviderPageBreadcrumbs />}
      buttonGroup={<ButtonGroup />}
    >
      <DataTable
        columns={columns}
        fetchUrl={fetchUrl}
        hideColumnsOnActive={['deleteDate', 'deleteUser']}
        hideColumnsOnDeleted={['actions']}
        itemUrl={itemUrl}
        keys={keys}
        title={intl.formatMessage({ id: 'providers.providers', defaultMessage: 'Proveedores' })}
        withTabs
      />
      <Dialog open={openDeleteDialog} onClose={() => toggleDeleteDialog()} fullWidth maxWidth="xs" aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{providersTexts.deleteTitleDialog}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {providersTexts.deleteContentDialog}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button text={providersTexts.cancel} onClick={() => toggleDeleteDialog()} />
          <Button
            text={providersTexts.delete}
            color="secondary"
            loading={deletingProvider}
            onClick={() => { handleDeleteProviders(providerID); }}
          />
        </DialogActions>
      </Dialog>
      <CustomDialogAlert
        toggleDialog={toggleNoLinkedWorkFieldDialog}
        openDialog={openNoLinkedWorkFieldDialog}
        message={noLinkedWorkFieldMessage}
      />

    </PageLayout>
  );
};

export default ProviderPage;
